// Minimal toast queue. Custom rather than a library because the codebase
// has no precedent for one and the surface area we need is tiny: a few
// brief auto-dismissing messages, optionally with an action button.
//
// Usage:
//   const { show } = useToast();
//   show({ message: 'Couldn’t sync 1 change', action: { label: 'Retry', onClick: retry } });

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const ToastContext = createContext(null);

const DEFAULT_DURATION_MS = 4000;

let nextId = 1;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const show = useCallback((toast) => {
    const id = nextId++;
    const duration = toast.durationMs ?? DEFAULT_DURATION_MS;
    setToasts((prev) => [...prev, { id, ...toast }]);
    if (duration > 0) {
      const timer = setTimeout(() => dismiss(id), duration);
      timersRef.current.set(id, timer);
    }
    return id;
  }, [dismiss]);

  useEffect(() => () => {
    for (const t of timersRef.current.values()) clearTimeout(t);
    timersRef.current.clear();
  }, []);

  const value = useMemo(() => ({ show, dismiss, toasts }), [show, dismiss, toasts]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} dismiss={dismiss} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
};

const viewportStyle = {
  position: 'fixed',
  bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 9999,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  pointerEvents: 'none',
  maxWidth: 'calc(100% - 2rem)',
  width: 'min(28rem, calc(100% - 2rem))',
};

// Inline styles on the visible elements — see project_chrome53_compat memory:
// inline > Tailwind on Chrome 53 WebView, and we want this UI to look right
// on Android 6 too.
const toastStyle = {
  pointerEvents: 'auto',
  width: '100%',
  background: 'var(--color-paper-bg)',
  border: '1px solid var(--color-ink-subtle)',
  boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
  borderRadius: '16px',
  padding: '12px 14px',
  display: 'flex',
  alignItems: 'center',
  fontFamily: 'inherit',
  color: 'var(--color-ink-main)',
};

const messageStyle = {
  flex: '1 1 auto',
  fontSize: '14px',
  lineHeight: '1.3',
  minWidth: 0,
};

const buttonStyle = {
  marginLeft: '12px',
  flex: '0 0 auto',
  fontSize: '13px',
  fontWeight: 500,
  background: 'transparent',
  border: '1px solid var(--color-ink-subtle)',
  color: 'var(--color-ink-main)',
  padding: '6px 10px',
  borderRadius: '8px',
  cursor: 'pointer',
};

function ToastViewport({ toasts, dismiss }) {
  if (toasts.length === 0) return null;
  return (
    <div style={viewportStyle} role="region" aria-live="polite" aria-label="Notifications">
      {toasts.map((t) => (
        <div key={t.id} style={toastStyle}>
          <div style={messageStyle}>{t.message}</div>
          {t.action ? (
            <button
              type="button"
              style={buttonStyle}
              onClick={() => {
                try { t.action.onClick(); } finally { dismiss(t.id); }
              }}
            >
              {t.action.label}
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export default ToastContext;
